/**
 * All roadmap dates are calendar dates stored as ISO strings ("YYYY-MM-DD").
 * There is no time-of-day and no timezone anywhere in the domain model.
 * These helpers are the ONLY place that converts between ISO strings and Date objects.
 */
import { addDays, differenceInCalendarDays, format, isValid, parseISO, startOfDay } from "date-fns";

export type ISODate = string; // "YYYY-MM-DD"

export const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isISODate(value: unknown): value is ISODate {
  return typeof value === "string" && ISO_DATE_RE.test(value) && isValid(parseISO(value));
}

export function toDate(iso: ISODate): Date {
  return startOfDay(parseISO(iso));
}

export function toISO(date: Date): ISODate {
  return format(date, "yyyy-MM-dd");
}

export function todayISO(): ISODate {
  return toISO(new Date());
}

export function addDaysISO(iso: ISODate, days: number): ISODate {
  return toISO(addDays(toDate(iso), days));
}

/** Signed number of calendar days from `a` to `b` (b - a). */
export function diffDays(a: ISODate, b: ISODate): number {
  return differenceInCalendarDays(toDate(b), toDate(a));
}

/** Inclusive duration in days: a bar from Mon to Mon is 1 day. */
export function durationDays(start: ISODate, end: ISODate): number {
  return diffDays(start, end) + 1;
}

export function maxISO(a: ISODate, b: ISODate): ISODate {
  return a >= b ? a : b;
}

export function minISO(a: ISODate, b: ISODate): ISODate {
  return a <= b ? a : b;
}

/**
 * Given a parent range and a percentage window, return the sub-range.
 * Used to place phases from templates ("business analysis = first 20%").
 * Guarantees end >= start and both inside the parent.
 */
export function rangeFromPercent(
  start: ISODate,
  end: ISODate,
  startPct: number,
  endPct: number,
): { startDate: ISODate; endDate: ISODate } {
  const total = durationDays(start, end);
  const s = Math.round((total * startPct) / 100);
  const e = Math.max(s, Math.round((total * endPct) / 100) - 1);
  return {
    startDate: addDaysISO(start, Math.min(s, total - 1)),
    endDate: addDaysISO(start, Math.min(e, total - 1)),
  };
}
